<template>
  <a-layout class="index animated fadeIn">
    <!-- 顶部返回栏 -->
    <a-card class="mb10">
      <a-button
        size="small"
        @click="
          () => {
            $router.go(-1);
          }
        "
      >
        <LeftOutlined /> 返回
      </a-button>
      <span class="ml15 card_title">{{ pageTitle }}</span>
    </a-card>

    <a-card :bordered="false">
      <!-- mode=add / edit / detail 三态共用；详情只读 -->
      <a-form
        ref="ruleForm"
        :model="form"
        :rules="rules"
        :label-col="{ span: 4 }"
        :wrapper-col="{ span: 12 }"
      >
        <a-form-item label="所属品牌" name="brand_id">
          <!-- 新增可选品牌；编辑/详情只读 -->
          <a-input
            v-model:value="form.brand_name"
            placeholder="请选择品牌"
            readonly
            style="width: 260px"
            :disabled="isDetail || isEdit"
          />
          <a-button
            type="primary"
            class="m-l-10"
            @click="brandVisible = true"
            :disabled="isDetail || isEdit"
          >
            选择
          </a-button>
        </a-form-item>

        <a-form-item label="设备号" name="device_mark">
          <a-input
            v-model:value="form.device_mark"
            placeholder="请输入设备号（品牌内唯一）"
            style="width: 320px"
            :readonly="isDetail"
          />
        </a-form-item>

        <a-form-item label="设备名" name="device_name">
          <a-input
            v-model:value="form.device_name"
            placeholder="请输入设备名（选填）"
            style="width: 320px"
            :readonly="isDetail"
          />
        </a-form-item>

        <a-form-item label="允许下载" name="allow_download">
          <a-radio-group v-model:value="form.allow_download" :disabled="isDetail">
            <a-radio :value="1">允许</a-radio>
            <a-radio :value="0">禁用</a-radio>
          </a-radio-group>
        </a-form-item>

        <!-- 详情态展示时间等只读信息 -->
        <template v-if="isDetail">
          <a-form-item label="品牌ID">
            <span>{{ form.brand_id || "-" }}</span>
          </a-form-item>
          <a-form-item label="创建时间">
            <span>{{ form.created_at || "-" }}</span>
          </a-form-item>
          <a-form-item label="更新时间">
            <span>{{ form.updated_at || "-" }}</span>
          </a-form-item>
          <a-form-item label="设备密钥">
            <span style="color: #999">密钥不在详情中展示，如需更换请使用「重置密钥」</span>
          </a-form-item>
        </template>

        <a-form-item :wrapper-col="{ span: 12, offset: 4 }">
          <a-button v-if="!isDetail" type="primary" :loading="submitting" @click="onSubmit">
            保存
          </a-button>
          <a-button class="m-l-10" @click="$router.go(-1)">
            {{ isDetail ? "返回" : "取消" }}
          </a-button>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 品牌选择弹窗 -->
    <a-modal
      v-model:visible="brandVisible"
      title="选择品牌"
      :width="1200"
      destroyOnClose
      centered
      :footer="null"
    >
      <selectBrand v-on:select="brandSelect" />
    </a-modal>

    <!-- 新增成功后密钥仅展示一次：点右上角关闭或「我已保存」都返回上一页 -->
    <a-modal
      v-model:visible="secretVisible"
      title="设备密钥（请立即保存）"
      :footer="null"
      centered
      :width="560"
      @cancel="onSecretOk"
    >
      <a-alert
        type="warning"
        show-icon
        message="密钥仅本次展示，关闭后无法再次查看，请立即复制并妥善保存。"
        class="m-b-15"
      />
      <a-typography-paragraph copyable :content="secretValue" />
      <div class="s_flex_center">
        <a-button type="primary" @click="onSecretOk">我已保存</a-button>
      </div>
    </a-modal>
  </a-layout>
</template>
<script>
import { ref, reactive, toRefs, inject, onMounted, computed } from "vue";
import { LeftOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { $iscode } from "@/utils/app";
import {
  codeWrapDeviceDetail,
  codeWrapDeviceCreate,
  codeWrapDeviceUpdate
} from "@/api/codeWrap";

export default {
  components: { LeftOutlined },
  setup() {
    const route = inject("$route");
    const $router = inject("$router");

    // 表单字段与接口 body 对齐
    const form = ref({
      brand_id: "",
      brand_name: "",
      device_mark: "",
      device_name: "",
      allow_download: 1
    });

    // 必填校验：brand_id / device_mark
    const rules = {
      brand_id: [{ required: true, message: "请选择品牌" }],
      device_mark: [{ required: true, message: "请输入设备号" }]
    };

    const state = reactive({
      submitting: false,
      brandVisible: false,
      secretVisible: false,
      secretValue: "",
      detail: {}
    });

    // mode 由路由路径推导：create / edit / detail（兼容 query.mode）
    const mode = computed(() => {
      const p = route.path || "";
      if (p.indexOf("/codeWrap/edit") > -1) return "edit";
      if (p.indexOf("/codeWrap/detail") > -1) return "detail";
      if (p.indexOf("/codeWrap/create") > -1) return "add";
      return route.query.mode || "add";
    });
    const isDetail = computed(() => mode.value === "detail");
    const isEdit = computed(() => mode.value === "edit");
    const isAdd = computed(() => mode.value === "add");
    const pageTitle = computed(() => {
      if (isDetail.value) return "设备授权详情";
      if (isEdit.value) return "编辑设备授权";
      return "新增设备授权";
    });

    // 品牌选择回调（selectBrand 单选 emit 单条）
    const brandSelect = (e) => {
      // 优先使用 ydd_brand.id，与接口 brand_id 定义一致
      form.value.brand_id = e.id;
      form.value.brand_name = e.brand_name || e.company || "";
      state.brandVisible = false;
    };

    // 从响应提取密钥（兼容对象 / 数组）
    const pickSecret = (payload) => {
      if (!payload) return "";
      if (typeof payload === "string") return payload;
      if (payload.device_secret) return payload.device_secret;
      if (Array.isArray(payload) && payload[0]) {
        return payload[0].device_secret || "";
      }
      return "";
    };

    // 详情 / 编辑前回填
    const initDetail = async () => {
      const id = route.query.id;
      if (!id) return;
      let res = await codeWrapDeviceDetail(id).then((res) => res.data);
      if ($iscode(res)) {
        // 兼容 data 为对象或数组
        const d = Array.isArray(res.data) ? res.data[0] : res.data;
        if (d) {
          state.detail = d;
          // brand_id / allow_download 可能是字符串数字，统一转 Number
          form.value.brand_id = d.brand_id === "" || d.brand_id == null ? "" : Number(d.brand_id);
          form.value.brand_name = d.brand_name || "";
          form.value.device_mark = d.device_mark || "";
          form.value.device_name = d.device_name || "";
          form.value.allow_download = Number(d.allow_download) === 0 ? 0 : 1;
        }
      }
    };

    const ruleForm = ref(null);

    // 提交：新增调 create，编辑调 update
    const onSubmit = () => {
      ruleForm.value
        .validate()
        .then(async () => {
          state.submitting = true;
          try {
            let res;
            if (isEdit.value) {
              // 编辑仅可改 device_mark / device_name / allow_download，不含 brand_id 与密钥
              // allow_download 统一转 Number，避免接口返回字符串数字
              const body = {
                device_mark: form.value.device_mark,
                device_name: form.value.device_name,
                allow_download: Number(form.value.allow_download)
              };
              res = await codeWrapDeviceUpdate(route.query.id, body).then((r) => r.data);
            } else {
              // 新增 body 字段与 create 接口一致
              const body = {
                brand_id: Number(form.value.brand_id),
                device_mark: form.value.device_mark,
                device_name: form.value.device_name,
                allow_download: Number(form.value.allow_download)
              };
              res = await codeWrapDeviceCreate(body).then((r) => r.data);
            }
            state.submitting = false;
            if ($iscode(res, true)) {
              if (isAdd.value) {
                // 新增成功：密钥仅返回一次，弹窗展示
                const secret = pickSecret(res.data);
                state.secretValue = secret || "（接口未返回密钥，请联系管理员）";
                state.secretVisible = true;
              } else {
                $router.go(-1);
              }
            }
          } catch (e) {
            state.submitting = false;
          }
        })
        .catch(() => {
          // 校验失败，antd 自动提示
        });
    };

    // 确认已保存密钥后返回列表
    const onSecretOk = () => {
      state.secretVisible = false;
      $router.go(-1);
    };

    onMounted(() => {
      if (isEdit.value || isDetail.value) {
        initDetail();
      }
    });

    return {
      form,
      rules,
      ruleForm,
      ...toRefs(state),
      mode,
      isDetail,
      isEdit,
      isAdd,
      pageTitle,
      brandSelect,
      onSubmit,
      onSecretOk
    };
  }
};
</script>
